import React, { useState } from "react";
import { Box, Paper, Typography, Button, Divider, Stack,Chip } from '@mui/material';

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { useApiClient } from "../../components/useApiClient";

//Add/Edit
import type { ArtworkPaymentScheduleSummary } from "../../models/Payments";

export type Props = {
    artworkId?: number;
    onAdd: () => void;
    reloadToken: number;
    onOpenSchedule?: (parentDiaryItemId: number) => void;
    onEditSchedule?: (paymentScheduleId: number) => void;
};

const PaymentsPanel: React.FC<Props> = (
    { artworkId, onAdd, reloadToken, onOpenSchedule, onEditSchedule }) => {

    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, isFinance, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;
    const hasFinanceRole = (isAdmin || isOwner || isFinance) && !isViewOnly;
    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const [stillLoading, setStillLoading] = useState(true);
    const [schedules, setSchedules] = useState<ArtworkPaymentScheduleSummary[]>([]);
    const [error, setError] = useState<string | null>(null);

    // *** Setup safe API Calls ***
    const { get } = useApiClient();

    async function getPaymentSchedulesByArtwork(artworkId: number) {

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in getPaymentSchedulesByArtwork');

            const res = await get<ArtworkPaymentScheduleSummary[]>(`payment-schedules/by-artwork/${artworkId}`, accessToken);
            return res;
        }
        catch (e) {
            console.error('Error in getPaymentSchedulesByArtwork', e);
            throw new Error('Error in getPaymentSchedulesByArtwork');
        }
    }

    React.useEffect(() => {
        if (artworkId) {
            let alive = true;
            (async () => {
                setStillLoading(true);
                setError(null);
                try {
                    const data = await getPaymentSchedulesByArtwork(artworkId);
                    if (!alive) return;
                    setSchedules(data ?? []);
                } catch (e: any) {
                    if (!alive) return;
                    setError("Failed to load payment schedules.");
                } finally {
                    if (alive) setStillLoading(false);
                }
            })();
            return () => { alive = false; };
        }
    }, [artworkId, reloadToken]);

    if (stillLoading) return (<div>Loading...</div>);

    return (
        <Paper elevation={3} square={false} sx={{ p: 2, width: "100%", boxSizing: "border-box" }}>
            <div className="section-header">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Payments</Typography>
                <button className="responsive-button" onClick={onAdd} hidden={true}>
                    Add Payment Schedule
                </button>
            </Box>
            </div>
            <Divider sx={{ my: 2 }} />

            {stillLoading ? (
                <Typography>Loading...</Typography>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : schedules.length === 0 ? (
                <Typography color="text.secondary">No payment schedules.</Typography>
            ) : (
                <Stack spacing={2}>
                                {schedules.map((s) => {
                                    const now = new Date();
                                    const due = s.nextDueUtc ? new Date(s.nextDueUtc) : null;
                                    const isDueToday = due && due.toDateString() === now.toDateString();
                                    const isOverdue = due && due < now && !isDueToday;
                                    
                                    return (
                                        <Box
                                            key={s.paymentScheduleId}
                                            sx={{
                                                p: 1.5,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                "&:hover": { bgcolor: "action.hover" },
                                            }}
                                            onClick={() => onOpenSchedule?.(s.parentDiaryItemId)}
                                        >


                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                justifyContent="space-between"
                                                alignItems={{ xs: "stretch", sm: "flex-start" }}
                                                spacing={2}
                                            >


                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontWeight: 700, whiteSpace: { xs: "normal", sm: "nowrap" } }} noWrap>
                                                        {s.title}
                                                    </Typography>

                                                    {isDueToday && (
                                                        <Chip
                                                            size="small"
                                                            color="warning"
                                                            label="Due Today"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}

                                                    {isOverdue && (
                                                        <Chip
                                                            size="small"
                                                            color="error"
                                                            label="Overdue"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}

                                                    <Typography variant="body2" color="text.secondary">
                                                        Outstanding: {Number(s.totalOutstanding).toFixed(2)} {s.currencyCode}
                                                        {"  "}•{"  "}
                                                        Paid: {Number(s.totalPaid).toFixed(2)} {s.currencyCode}
                                                        {"  "}•{"  "}
                                                        Total: {Number(s.totalAmount).toFixed(2)} {s.currencyCode}
                                                    </Typography>

                                                    <Typography variant="body2" color="text.secondary">
                                                        Milestones: {s.paidMilestoneCount} paid / {s.plannedMilestoneCount} planned
                                                        {s.nextDueUtc ? `  •  Next due: ${new Date(s.nextDueUtc).toLocaleDateString()}` : ""}
                                                    </Typography>
                                                </Box>



                                                <Stack
                                                    direction="column"
                                                    spacing={1}
                                                    flexShrink={0}
                                                    justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenSchedule?.(s.parentDiaryItemId);
                                                        }}
                                                        disabled={!onOpenSchedule}
                                                    >
                                                        Open
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onEditSchedule?.(s.paymentScheduleId);
                                                        }}
                                                        disabled={!onEditSchedule || !hasFinanceRole}
                                                    >
                                                        Edit
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            <div className="section-header">
                <h3>Payments History</h3>
            </div>
        </Paper>
    );
}

export default PaymentsPanel;
